import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { safeNextPath } from '@/code-theme/routes';

/**
 * Sign in as a page (`/entrar?next=/loja`).
 *
 * Why: a store on a code theme never asks for the password on top of the
 * theme; it sends the player here, a new document without the theme, and
 * back to `next` (a path of this store, never another site) after signing in.
 * Any store can link here too.
 */
export default function SignInPage() {
  const { t } = useTranslation();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { store } = useStore();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNextPath(params.get('next'));

  useEffect(() => {
    if (!isAuthenticated) return;
    // Back to the theme in a new document; other layouts just change page.
    if (store?.template === 'code') window.location.replace(next);
    else navigate(next, { replace: true });
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) openLoginModal();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="container mx-auto max-w-md px-4 py-24 text-center" data-testid="sign-in-page">
      <h1 className="font-display mb-3 text-3xl font-bold text-foreground">{t('store.signIn.title')}</h1>
      <p className="mb-8 text-muted-foreground">{t('store.signIn.text')}</p>
      <div className="flex flex-col items-center gap-4">
        <Button onClick={openLoginModal} data-testid="sign-in-open">
          {t('store.header.enter')}
        </Button>
        <Link to="/" className="text-sm text-muted-foreground hover:underline">
          {t('store.signIn.back')}
        </Link>
      </div>
    </div>
  );
}
